/* eslint-disable react-hooks/exhaustive-deps, react-hooks/set-state-in-effect */
import { faFileLines } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Button, Stack, styled, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { MonthCalendar, MonthCalendarDayBlock, MonthCalendarPicker, RefreshButton, useAppBar } from "../../../components";
import { AttendanceLogEntry, CalendarEvent, Child, DailyInspection } from "../../../models";
import { useGetAllAttendanceLogEntriesFilteredQuery, useGetAllChildrenFilteredQuery, useGetDailyInspectionsFilteredQuery } from "../../../redux/apiStore";
import { CalendarDailyInspectionEvent } from "./CalendarDailyInspectionEvent";
import { CalendarDayAttendanceEvent } from "./CalendarDayAttendanceEvent";
import DayDetailDrawer from "./DayDetailDrawer";
import { GenerateInspectionReportDrawer } from "./GenerateInspectionReportDrawer";

const CalendarContainer = styled(Box)({});

const createDaycareClosedEvent = (date: Dayjs): CalendarEvent => {
  return {
    date,
    content: <MonthCalendarDayBlock key={date.format("d")} label="Daycare Closed"></MonthCalendarDayBlock>,
  };
};

const createDailyInspectionEvent = (date: Dayjs, dailyInspection: DailyInspection | undefined, today: Dayjs): CalendarEvent => {
  return {
    date,
    content: <CalendarDailyInspectionEvent inspectionCentralDate={date} dailyInspection={dailyInspection} today={today} />,
  };
};

const createAttendanceEvents = (date: Dayjs, children: Child[], attendanceLogEntries: AttendanceLogEntry[]): CalendarEvent[] => {
  const activeChildIds = children.filter((x) => x.isActive).map((x) => x.id);
  const childIds = [...activeChildIds, ...attendanceLogEntries.map((x) => x.childId)];
  const childrenToList = children.filter((x) => childIds.includes(x.id));
  const today = dayjs();
  const isFuture = !!date && date.isAfter(today);
  if (attendanceLogEntries.length === 0 && isFuture) {
    return [];
  }
  return _.orderBy(childrenToList, "firstName", "asc").map((child) => {
    const attendanceLogEntry = attendanceLogEntries.find((x) => x.childId === child.id);
    return {
      date,
      content: <CalendarDayAttendanceEvent key={child.id} child={child} attendanceLog={attendanceLogEntry} />,
    };
  });
};

export function CalendarPage() {
  const appBar = useAppBar();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const calendarRef = useRef<{ scrollToToday: () => void }>(null);
  const today = dayjs();
  const [month, setMonth] = useState<number>(today.month());
  const [year, setYear] = useState<number>(today.year());
  const [rangeStartMonth, setRangeStartMonth] = useState<Dayjs>(dayjs(new Date(year, month, 1)).startOf("month"));
  const [rangeEndMonth, setRangeEndMonth] = useState<Dayjs>(dayjs(new Date(year, month, 1)).startOf("month"));
  const [inspectionReportDrawerOpen, setInspectionReportDrawerOpen] = useState<boolean>(false);
  const firstDayOfRange = useMemo(() => rangeStartMonth.startOf("month"), [rangeStartMonth]);
  const lastDayOfRange = useMemo(() => rangeEndMonth.endOf("month"), [rangeEndMonth]);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [editing, setEditing] = useState<boolean>(false);
  const [editingDate, setEditingDate] = useState<Dayjs | null>(null);
  const getAttendanceLogEntries = useGetAllAttendanceLogEntriesFilteredQuery({
    rangeStartDate: firstDayOfRange.format("YYYY-MM-DD"),
    rangeEndDate: lastDayOfRange.format("YYYY-MM-DD HH:mm:ss"),
  });
  const dailyInspections = useGetDailyInspectionsFilteredQuery({
    rangeStartInspectionDate: firstDayOfRange.format("YYYY-MM-DD"),
    rangeEndInspectionDate: lastDayOfRange.format("YYYY-MM-DD HH:mm:ss"),
  });
  const children = useGetAllChildrenFilteredQuery({
    showInactive: false,
  });

  const handleChangeMonth = (month: number, year: number) => {
    setMonth(month);
    setYear(year);

    if (!isSmallScreen) {
      const targetMonth = dayjs(new Date(year, month, 1)).startOf("month");
      setRangeStartMonth(targetMonth);
      setRangeEndMonth(targetMonth);
    }
  };

  const handleMobileRangeChange = useCallback((startMonth: Dayjs, endMonth: Dayjs) => {
    setRangeStartMonth((currentStart) => {
      if (currentStart.isSame(startMonth, "month")) return currentStart;
      return startMonth.startOf("month");
    });

    setRangeEndMonth((currentEnd) => {
      if (currentEnd.isSame(endMonth, "month")) return currentEnd;
      return endMonth.startOf("month");
    });
  }, []);

  useEffect(() => {
    appBar.setDefaults();
    appBar.setTitle("Calendar");
    const rightContent = (
      <Stack direction="row" gap={1}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setInspectionReportDrawerOpen(true)}
          startIcon={<FontAwesomeIcon icon={faFileLines} />}
          sx={{ height: 50 }}
        >
          Report
        </Button>
        <RefreshButton
          onRefresh={() => {
            Promise.all([getAttendanceLogEntries.refetch(), dailyInspections.refetch(), children.refetch()]);
          }}
        />
        {isSmallScreen ? (
          <Button size="small" variant="text" onClick={() => calendarRef.current?.scrollToToday()}>
            Today
          </Button>
        ) : (
          <MonthCalendarPicker month={month} year={year} onChange={handleChangeMonth} />
        )}
      </Stack>
    );
    appBar.setRightContent(rightContent);
  }, [month, year, isSmallScreen]);

  // Auto-scroll to today on page load for mobile
  useEffect(() => {
    if (!isSmallScreen) return;

    // Use a longer delay to ensure all layout calculations are complete
    // and measurements (like app bar height) are accurate
    const timer = setTimeout(() => {
      calendarRef.current?.scrollToToday();
    }, 500);

    return () => clearTimeout(timer);
  }, [isSmallScreen]);

  useEffect(() => {
    const start = firstDayOfRange; // first day of loaded range
    const end = lastDayOfRange; // last day of loaded range

    let events = [] as CalendarEvent[];

    let current = start;

    while (current.isBefore(end) || current.isSame(end, "day")) {
      const formattedDate = current.format("YYYY-MM-DD");
      const inspection = dailyInspections?.data?.find((x) => x.inspectionCentralDateTime?.startsWith(formattedDate));

      const isWeekend = [0, 6].includes(current.day());
      const isClosed = isWeekend || inspection?.isClosed; // account for daily inspection closure

      if (isClosed) {
        events.push(createDaycareClosedEvent(current));
      } else {
        events.push(createDailyInspectionEvent(current, inspection, today));
        let logs = getAttendanceLogEntries.data ?? [];
        logs = logs.filter((x) => {
          const d = dayjs(x.timeInCentralDateTime);
          return d.format("YYYY-MM-DD") === formattedDate;
        });
        events = [...events, ...createAttendanceEvents(current, children?.data ?? [], logs)];
      }

      current = current.add(1, "day");
    }

    setEvents(events);
  }, [firstDayOfRange, lastDayOfRange, getAttendanceLogEntries.data, dailyInspections.data, children.data]);

  const handleDateClick = (date: Dayjs) => {
    const isWeekend = [0, 6].includes(date.day());
    if (isWeekend) return;
    setEditingDate(date);
    setEditing(true);
  };

  return (
    <CalendarContainer sx={{ p: 2, height: "100%", display: "flex", flexDirection: "column" }}>
      {editingDate && (
        <DayDetailDrawer
          selectedDate={editingDate}
          dailyInspection={dailyInspections.data?.find((x) => x.inspectionCentralDateTime?.startsWith(editingDate.format("YYYY-MM-DD")))}
          attendanceLogEntries={(getAttendanceLogEntries.data ?? []).filter(
            (x) => dayjs(x.timeInCentralDateTime).format("YYYY-MM-DD") === editingDate.format("YYYY-MM-DD"),
          )}
          children={children.data ?? []}
          isOpen={editing}
          onClose={() => setEditing(false)}
        />
      )}
      <GenerateInspectionReportDrawer open={inspectionReportDrawerOpen} onClose={() => setInspectionReportDrawerOpen(false)} />
      <MonthCalendar ref={calendarRef} month={month} year={year} events={events} onDateClick={handleDateClick} onMobileRangeChange={handleMobileRangeChange} />
    </CalendarContainer>
  );
}
